import { useRecruitmentApi } from 'infrastructure/api';
import { useBaseCrudService } from 'framework/crud';
import { useVacancyGroupId } from 'domain/recruiting/newVacancy/routing';
import vacancyPublicationValidation from 'domain/recruiting/newVacancy/show/publications/createupdate/vacancyPublicationValidation';
import { VACANCIES_GROUPS } from 'domain/shared/newVacancy';
import { FormFieldsData } from 'framework/form';
import { camelCase } from 'lodash';
import {
  VacancyGroup,
  VacancyPublicationDetailsPayload,
  VacancyPublicationFormField,
  VacancyPublicationPayload,
} from './types';

export const PUBLICATION_PLATFORM = {
  SYCOM: 'Syberry.com',
};

export const ENGLISH_LEVEL_OPTIONS = {
  BEGINNER: 'Beginner',
  ELEMENTARY: 'Elementary',
  INTERMEDIATE: 'Intermediate',
  'UPPER INTERMEDIATE': 'Upper Intermediate',
  ADVANCED: 'Advanced',
  PROFICIENCY: 'Proficiency',
};
export const PUBLICATION_STATUS = {
  OPEN: 'Open',
  CLOSED: 'Closed',
  DRAFT: 'Draft',
};

const useVacancyPublicationService = () => {
  const groupId = useVacancyGroupId();
  const { sendPatchRequest, sendGetRequest } = useRecruitmentApi();

  const {
    search: baseSearch,
    create: baseCreate,
    update: baseUpdate,
    getById: baseGetById,
  } = useBaseCrudService({
    listResourceUrl: '/vacancies/publications',
    singleResourceUrl: '/vacancies/publications/:id',
    useService: useRecruitmentApi,
  });

  const mapToVacancyPublication = (formData: Pick<VacancyPublicationPayload, 'formFields'>) => {
    const data = { ...formData };

    if (data.formFields) {
      data.formFields = data.formFields.map(formField => ({
        ...formField,
        fieldName: camelCase(formField.value),
        label: formField.value,
      }));
    }

    return data;
  };

  const search = () => {
    return baseSearch({
      filter: {
        'vacancyGroup.id': {
          eq: groupId,
        },
      },
    });
  };

  const mapOfficeToLocation = (office: number) => {
    switch (office) {
      case 1:
        return { country: 'US', city: 'Austin' };
      case 2:
        return { country: 'BY', city: 'Minsk' };
      case 3:
        return { country: 'PL', city: 'Krakow' };
      case 4:
        return { country: 'GE', city: 'Tbilisi' };
      case 5:
      default:
        return null;
    }
  };

  const buildVacancyDetails = (payloadDetails: VacancyPublicationDetailsPayload) => [
    {
      id: payloadDetails.id || null,
      location: mapOfficeToLocation(Number(payloadDetails.location)),
      assignmentType: payloadDetails.assignmentType,
      workingSchedule: payloadDetails.workingSchedule,
      workplaceLocation: payloadDetails.workplaceLocation,
      contractType: payloadDetails.contractType,
      relocationOption: payloadDetails.relocationOption,
    },
  ];

  const processFormFields = (formFields?: VacancyPublicationFormField[]) => {
    return formFields && formFields.length > 0 ? mapToVacancyPublication({ formFields }).formFields : [];
  };

  const create = async (payload: FormFieldsData) => {
    const vacancyDetails = buildVacancyDetails(payload.details);
    const processedFormFields = processFormFields(payload.formFields);

    return baseCreate({
      ...payload,
      details: undefined,
      publicationDetails: vacancyDetails,
      formFields: processedFormFields,
    });
  };

  const update = async (id: string, payload: FormFieldsData) => {
    const vacancyDetails = buildVacancyDetails(payload.details);
    const processedFormFields = processFormFields(payload.formFields);

    return baseUpdate(id, {
      ...payload,
      details: undefined,
      publicationDetails: vacancyDetails,
      formFields: processedFormFields,
    });
  };

  const getVacancyGroup = async (id: string): Promise<VacancyGroup | null> => {
    try {
      const response = await sendGetRequest(`${VACANCIES_GROUPS}/${id}`);
      return await response.json();
    } catch {
      return null;
    }
  };

  const getById = async (id: string) => {
    const result = await baseGetById(id);
    const vacancyGroup = await getVacancyGroup(result.vacancyGroupId);

    return {
      ...result,
      vacancyGroupName: vacancyGroup?.specialization ?? result.vacancyGroupAlias ?? null,
    };
  };

  const constructVacancyPublicationDescription = async (id: string) => {
    const vacancyGroup = await getVacancyGroup(id);

    return {
      vacancyGroupId: id,
      name: vacancyGroup?.specialization ?? null,
    };
  };

  const changeStatus = async (vacancyGroupId: string, status: string) => {
    const result = await sendPatchRequest(`/vacancies/publications/${vacancyGroupId}`, { status });
    return result.json();
  };

  const updateStatus = async (vacancyGroupId: string, priority: string) => {
    const vacancy = await changeStatus(vacancyGroupId, priority);

    return vacancy;
  };

  const getValidationSchema = () => Promise.resolve(vacancyPublicationValidation);

  return {
    constructVacancyPublicationDescription,
    search,
    create,
    getValidationSchema,
    getById,
    update,
    updateStatus,
    searchWithFilters: baseSearch,
  };
};

export default useVacancyPublicationService;
